<template>
  <div class="console-type-bar">
    <button
      type="button"
      class="bar-action bar-action--type"
      @mousedown.stop.prevent
      @click.stop="onTypeClick"
      @mouseenter="onTypeEnter"
      @mouseleave="hideTooltip"
    >
      <span v-if="typeof typeIcon === 'string'" class="bar-emoji">{{
        typeIcon
      }}</span>
      <component :is="typeIcon" v-else class="bar-icon" />
      <span class="bar-label">{{ typeName }}</span>
      <ChevronDownIcon class="bar-chevron" />
    </button>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  type Component,
} from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import { getTypeIcon } from "@/components/databases";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";
import { useTooltip, type TooltipContent } from "@/components/tooltip";

export default defineComponent({
  name: "ConsoleTypeBar",
  components: {
    ChevronDownIcon,
  },
  props: {
    type: { type: Object as PropType<GraphDatabase>, required: true },
    // Display name overriding the database name on the type pill. The default
    // types (Task/Event/Page) are represented by their extensions database,
    // whose name may differ from the user-facing type name.
    typeLabel: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  emits: {
    "select-type": (_type: GraphDatabase, _element?: HTMLElement) => true,
  },
  setup(props, { emit }) {
    const profile = inject.profile();
    const typeName = computed(
      () => props.typeLabel ?? (props.type.data_name || props.type.name)
    );
    const typeIcon = computed<Component | string>(() =>
      getTypeIcon(profile.value, props.type)
    );

    const elementFrom = (event: MouseEvent): HTMLElement | undefined =>
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : undefined;

    const tooltipEl = ref<HTMLElement | undefined>(undefined);
    const tooltipContent = ref<TooltipContent>(undefined);
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(tooltipEl, tooltipContent, "top");
    onBeforeUnmount(releaseTooltip);
    const onTypeEnter = (event: MouseEvent) => {
      const element = elementFrom(event);
      if (element) {
        tooltipEl.value = element;
        tooltipContent.value = { text: "Switch type", shortcut: "as" };
        showTooltip();
      }
    };
    const onTypeClick = (event: MouseEvent) => {
      hideTooltip();
      emit("select-type", props.type, elementFrom(event));
    };

    return {
      hideTooltip,
      onTypeClick,
      onTypeEnter,
      typeIcon,
      typeName,
    };
  },
});
</script>

<style scoped lang="scss">
.console-type-bar {
  display: flex;
  align-items: center;
  padding: 0 10px 20px;
}

.bar-action {
  @extend %font-caption-regular;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 3px 8px;
  border: 1px solid transparent;
  border-radius: $border-radius-m;
  cursor: default;
  @include transition(background-color 0.1s ease-out, color 0.1s ease-out);
  @include themed() {
    background-color: transparent;
    color: t($grey-70);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.bar-action--type .bar-label {
  @extend %font-caption-medium;
}

.bar-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;

  :deep(svg) {
    display: block;
    width: 13px;
    height: 13px;
  }
}

.bar-chevron {
  width: 8px;
  height: 8px;
  margin-inline-start: -1px;
  flex-shrink: 0;

  :deep(.fill) {
    @include themed() {
      fill: t($grey-50);
    }
  }
}

.bar-emoji {
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
}
</style>
